<template>
  <div>
    <!-- Faixa: Valor acumulado + Seletor de período -->
    <div class="row">
      <div class="col-12 col-lg-12 mb-3">
        <div class="card bg-light-gray top-summary-card">
          <div class="card-body py-0">
            <div class="row text-center">
              <div class="order-1 order-lg-0 col-12 col-lg-6 border-right-lg p-3">
                <h5 class="mb-1">Valor acumulado de prêmios no período</h5>
                <div class="font-28"><strong>{{ formatCurrencyLarge(accumulated_value) }}</strong></div>
              </div>
              <div class="order-0 order-lg-1 col-12 col-lg-6 border-bottom no-border-bottom-lg p-3">
                <h5 class="mb-2">
                  Selecione o período
                  <button type="button" class="btn btn-sm btn-outline-secondary ml-1" @click="applyCustomPeriod">
                    <i class="mdi mdi-dots-vertical"></i>
                  </button>
                </h5>
                <div class="d-flex justify-content-center gap-1">
                  <div class="period-switch" role="group" aria-label="Período">
                    <template v-if="activeTab === 'realizadas'">
                      <button
                        type="button"
                        class="btn period-switch-btn"
                        :class="selectedPeriod === -120 ? 'period-switch-btn--active' : ''"
                        @click="setPeriod(-120)"
                      >-120 dias</button>
                      <button
                        type="button"
                        class="btn period-switch-btn"
                        :class="selectedPeriod === -60 ? 'period-switch-btn--active' : ''"
                        @click="setPeriod(-60)"
                      >-60 dias</button>
                      <button
                        type="button"
                        class="btn period-switch-btn"
                        :class="selectedPeriod === -30 ? 'period-switch-btn--active' : ''"
                        @click="setPeriod(-30)"
                      >-30 dias</button>
                      <button
                        type="button"
                        class="btn period-switch-btn"
                        :class="selectedPeriod === 0 ? 'period-switch-btn--active' : 'period-switch-btn--today'"
                        @click="setPeriod(0)"
                      >Hoje</button>
                    </template>
                    <template v-else>
                      <button
                        type="button"
                        class="btn period-switch-btn"
                        :class="selectedPeriod === 0 ? 'period-switch-btn--active' : 'period-switch-btn--today'"
                        @click="setPeriod(0)"
                      >Hoje</button>
                      <button
                        type="button"
                        class="btn period-switch-btn"
                        :class="selectedPeriod === 30 ? 'period-switch-btn--active' : ''"
                        @click="setPeriod(30)"
                      >+30 dias</button>
                      <button
                        type="button"
                        class="btn period-switch-btn"
                        :class="selectedPeriod === 60 ? 'period-switch-btn--active' : ''"
                        @click="setPeriod(60)"
                      >+60 dias</button>
                      <button
                        type="button"
                        class="btn period-switch-btn"
                        :class="selectedPeriod === 90 ? 'period-switch-btn--active' : ''"
                        @click="setPeriod(90)"
                      >+90 dias</button>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <br>

    <!-- Top 10 maiores prêmios -->
    <div class="card bg-light-gray mt-0" style="border-radius: 0; border-top: none;">
      <div class="card-header bg-light-gray position-relative">
        <h5 class="text-center my-0">Top 10 maiores prêmios do período</h5>
        <div class="d-flex justify-content-center my-2 position-absolute" style="top: 0.5rem; right: 1rem;">
          <b-dropdown variant="outline-secondary" size="sm" right>
            <template #button-content>
              <span>{{ selectedTopPremiumPeriodLabel }}</span>
              <i class="fa fa-angle-down ml-1"></i>
            </template>
            <b-dropdown-item @click="setTopPremiumPeriod(null)">Total</b-dropdown-item>
            <b-dropdown-item @click="setTopPremiumPeriod(30)">30 dias</b-dropdown-item>
            <b-dropdown-item @click="setTopPremiumPeriod(60)">60 dias</b-dropdown-item>
            <b-dropdown-item @click="setTopPremiumPeriod(90)">90 dias</b-dropdown-item>
            <b-dropdown-item @click="setTopPremiumPeriod(120)">120 dias</b-dropdown-item>
          </b-dropdown>
        </div>
      </div>
      <div v-show="showTop10" class="card-body pt-0">
        <div class="table-responsive">
          <table class="table-v3">
            <thead>
              <tr>
                <th class="nowrap sortable text-left">ID</th>
                <th class="nowrap sortable">IS</th>
                <th class="nowrap sortable">TOMADOR</th>
                <th class="nowrap sortable sorting-desc">PRÊMIO</th>
                <th class="nowrap sortable text-center">EM RENOVAÇÃO</th>
                <th class="nowrap sortable text-center">STATUS</th>
                <th class="nowrap text-right"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="policy in top_premiums" :key="policy.id">
                <td class="text-left font-12 nowrap">{{ policy.number }}</td>
                <td class="font-12 nowrap">{{ formatCurrency(policy.coverage_value) }}</td>
                <td class="font-12">{{ policy.policy_holder_name }}</td>
                <td class="font-12 nowrap">{{ formatCurrency(policy.insurance_premium) }}</td>
                <td class="font-12 text-center">
                  <i :class="policy.renovation_id ? 'fa fa-check text-success font-18' : 'fa fa-clock text-orange font-16'"></i>
                </td>
                <td class="font-12 text-center">
                  <span class="btn btn-sm status-badge text-uppercase rounded-pill nowrap d-block" :class="getStatusBadgeClass(policy.status)">
                    {{ getNameColumn(policy.status) }}
                  </span>
                </td>
                <td class="text-right font-12">
                  <a class="text-dark" href="#" @click.prevent="$can('policy-show') && redirect('details-policy', { policyId: policy.id })"><i class="fa fa-eye"></i></a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Apólices próximas da data de renovação -->
    <div class="card bg-light-gray mt-3">
      <div class="card-header bg-light-gray">
        <h5 class="text-center my-0">Apólices próximas da data de renovação</h5>
      </div>
      <div class="card-body pt-0">
        <div class="table-responsive mt-4">
          <table class="table-v3">
            <thead>
              <tr>
                <th class="nowrap sortable text-left">ID</th>
                <th class="nowrap sortable">IS</th>
                <th class="nowrap sortable">Tomador</th>
                <th class="nowrap sortable">Segurado</th>
                <th class="nowrap sortable">Prêmio</th>
                <th class="nowrap sortable sorting-desc text-center">Dias para renovação</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="policy in upcoming_renewals" :key="policy.id">
                <td class="text-left font-12 nowrap">{{ policy.number }}</td>
                <td>{{ formatCurrency(policy.coverage_value) }}</td>
                <td class="font-12">{{ policy.policy_holder_name }}</td>
                <td>{{ policy.insured_name }}</td>
                <td class="font-12 nowrap">{{ formatCurrency(policy.insurance_premium) }}</td>
                <td class="text-center font-12 nowrap">
                  <span class="btn btn-sm status-badge text-uppercase rounded-pill nowrap" :class="getDaysBadgeClass(policy.days_to_renewal)">
                    {{ policy.days_to_renewal }} dias
                  </span>
                </td>
                <td class="text-right font-12">
                  <button class="btn btn-sm btn-outline-danger text-uppercase">
                    <i class="fa fa-times mr-1"></i> Cancelar renovação
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="has_more_renewals" class="text-center py-1">
          <button class="btn btn-outline-primary" @click="loadMoreUpcomingRenewals">
            Carregar mais...
          </button>
        </div>
      </div>
    </div>

    <!-- Linha inferior: Top 10 clientes + Resumo por mês -->
    <div class="row mt-4" v-if="!isClientHolder()">
      <!-- Top 10 clientes -->
      <div class="col-12 col-xl-6">
        <div class="card bg-light-gray mb-3">
          <div class="card-header bg-light-gray position-relative">
            <h5 class="text-center my-0">Top 10 clientes</h5>
            <div class="d-flex justify-content-center my-2 position-absolute" style="top: 0.5rem; right: 1rem;">
              <b-dropdown variant="outline-secondary" size="sm" right>
                <template #button-content>
                  <span>{{ selectedClientPeriodLabel }}</span>
                  <i class="fa fa-angle-down ml-1"></i>
                </template>
                <b-dropdown-item @click="setClientPeriod(null)">Total</b-dropdown-item>
                <b-dropdown-item @click="setClientPeriod(30)">30 dias</b-dropdown-item>
                <b-dropdown-item @click="setClientPeriod(60)">60 dias</b-dropdown-item>
                <b-dropdown-item @click="setClientPeriod(90)">90 dias</b-dropdown-item>
                <b-dropdown-item @click="setClientPeriod(120)">120 dias</b-dropdown-item>
              </b-dropdown>
            </div>
          </div>
          <div class="card-body pt-0">
            <div class="table-responsive">
              <table class="table-v3">
                <thead>
                  <tr>
                    <th class="sortable text-left">#</th>
                    <th class="sortable text-center">Cliente</th>
                    <th class="sortable text-center">Quantidade de Apólices</th>
                    <th class="sortable text-center">Valor acumulado de prêmios</th>
                    <th class="text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="client in top_clients" :key="client.rank">
                    <td class="text-left font-12 nowrap">{{ client.rank }}</td>
                    <td class="text-center font-12">{{ client.client_name }}</td>
                    <td class="text-center font-12 nowrap">{{ client.policy_count }}</td>
                    <td class="text-center font-12 nowrap"><strong>{{ formatCurrency(client.total_premium) }}</strong></td>
                    <td class="text-right font-12"><a class="text-dark" href="#" @click.prevent><i class="fa fa-eye"></i></a></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Resumo por mês (Comissão e Apólices) -->
      <div class="col-12 col-xl-6">
        <div class="card bg-light-gray mb-3">
          <div class="card-header bg-light-gray">
            <h5 class="text-center">Resumo por mês (Comissão e Apólices)</h5>
            <div class="d-flex justify-content-center">
              <div class="input-group my-2 w-auto">
                <div class="input-group-prepend">
                  <span class="input-group-text border-right-0">Ano</span>
                  <select v-model="selectedYear" @change="getMonthlyCommissionSummary" class="custom-select">
                    <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div class="card-body pt-0">
            <div class="table-responsive">
              <table class="table-v3">
                <thead>
                  <tr>
                    <th class="sortable sorting-asc text-left">Mês</th>
                    <th class="sortable text-center">Soma de Comissão</th>
                    <th class="sortable text-center">Contagem de Apólice</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in monthly_summary" :key="item.month">
                    <td class="text-left font-12 nowrap">{{ item.month }}</td>
                    <td class="text-center font-12 nowrap"><strong>{{ formatCurrency(item.commission_total) }}</strong></td>
                    <td class="text-center font-12 nowrap">{{ item.policy_count }}</td>
                    <td class="text-right font-12"><a class="text-dark" href="#" @click.prevent><i class="fa fa-eye"></i></a></td>
                  </tr>
                  <tr v-if="monthly_summary.length > 0" class="font-weight-bold monthly-total-row">
                    <td class="text-left font-12 nowrap"><strong>Total</strong></td>
                    <td class="text-center font-12 nowrap"><strong>{{ formatCurrency(total_commission) }}</strong></td>
                    <td class="text-center font-12 nowrap"><strong>{{ total_policies.toLocaleString('pt-BR') }}</strong></td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <br>

    <b-modal
      id="modal-periodo-renovacoes"
      v-model="periodModalVisible"
      title="Definir Período Customizado"
      centered
      hide-header-close
      no-close-on-backdrop
      no-close-on-esc
    >
      <div class="mb-3">
        <label class="period-modal-label">Data de Início</label>
        <input v-model="customPeriodStart" type="date" class="form-control" />
      </div>
      <div class="mb-2">
        <label class="period-modal-label">Data de Fim</label>
        <input v-model="customPeriodEnd" type="date" class="form-control" />
      </div>

      <template #modal-footer>
        <button type="button" class="btn btn-secondary px-4" @click="periodModalVisible = false">
          <i class="fa fa-times mr-1"></i> Cancelar
        </button>
        <button type="button" class="btn btn-primary px-4" @click="confirmCustomPeriod">
          <i class="fa fa-search mr-1"></i> Aplicar
        </button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import authenticationService from "@/services/authentication";
import policyService from "@/services/policy";
import numericFormatterMixin from "@/mixins/numericFormatter";
import commomsHelper from "@/mixins/commomsHelper";
import translateStatusQuotationTypes from "@/enums/translate/statusQuotationTypes";

export default {
  name: "DashboardPolicyRenewal",
  mixins: [numericFormatterMixin, commomsHelper],
  props: {
    activeTab: {
      type: String,
      default: "realizadas",
    },
    customStartDate: {
      type: String,
      default: "",
    },
    customEndDate: {
      type: String,
      default: "",
    },
  },
  data() {
    const currentYear = new Date().getFullYear();
    return {
      translateStatusQuotationTypes: translateStatusQuotationTypes,
      selectedPeriod: -30,
      accumulated_value: 0,
      top_premiums: [],
      top_clients: [],
      upcoming_renewals: [],
      has_more_renewals: false,
      monthly_summary: [],
      total_commission: 0,
      total_policies: 0,
      selectedYear: currentYear,
      selectedClientPeriod: null,
      selectedTopPremiumPeriodLabel: "Total",
      selectedTopPremiumPeriodDays: null,
      selectedClientPeriodLabel: "Total",
      upcomingRenewalsLimit: 5,
      showTop10: true,
      periodModalVisible: false,
      customPeriodStart: "",
      customPeriodEnd: "",
      useCustomPeriod: false,
      yearOptions: Array.from({ length: 6 }, (_, i) => currentYear - i),
    };
  },
  watch: {
    activeTab(newTab) {
      this.selectedPeriod = newTab === 'realizadas' ? -30 : 30;
      this.getDashboardPoliciesDue();
      this.getUpcomingRenewals();
    },
  },
  created() {
    this.selectedPeriod = this.activeTab === 'realizadas' ? -30 : 30;
    this.getDashboardPoliciesDue();
    this.getTopClients();
    this.getMonthlyCommissionSummary();
    this.getUpcomingRenewals();
  },
  methods: {
    getDateParams() {
      if (this.selectedTopPremiumPeriodDays) {
        const today = new Date();
        const fmt = (d) => d.toISOString().split('T')[0];

        if (this.activeTab === 'realizadas') {
          const start = new Date(today);
          start.setDate(today.getDate() - this.selectedTopPremiumPeriodDays);
          return { custom_start_date: fmt(start), custom_end_date: fmt(today) };
        }

        const end = new Date(today);
        end.setDate(today.getDate() + this.selectedTopPremiumPeriodDays);
        return { custom_start_date: fmt(today), custom_end_date: fmt(end) };
      }

      if (this.useCustomPeriod && this.customPeriodStart && this.customPeriodEnd) {
        return { custom_start_date: this.customPeriodStart, custom_end_date: this.customPeriodEnd };
      }

      if (this.customStartDate && this.customEndDate) {
        return { custom_start_date: this.customStartDate, custom_end_date: this.customEndDate };
      }
      const today = new Date();
      const fmt = (d) => d.toISOString().split('T')[0];

      if (this.activeTab === 'realizadas') {
        const start = new Date(today);
        start.setDate(today.getDate() + this.selectedPeriod);
        return { custom_start_date: fmt(start), custom_end_date: fmt(today) };
      } else {
        const end = new Date(today);
        end.setDate(today.getDate() + this.selectedPeriod);
        return { custom_start_date: fmt(today), custom_end_date: fmt(end) };
      }
    },

    setPeriod(period) {
      this.selectedPeriod = period;
      this.useCustomPeriod = false;
      this.selectedTopPremiumPeriodDays = null;
      this.selectedTopPremiumPeriodLabel = "Total";
      this.getDashboardPoliciesDue();
    },

    getDashboardPoliciesDue() {
      this.$emit("update-loading", true);
      policyService
        .getDashboardPoliciesDue(this.getDateParams())
        .then((response) => {
          this.top_premiums = response.data.policies;
          this.accumulated_value = response.data.accumulated_value;
        })
        .catch(() => {
          this.$emit("show-error", "Não foi possível recuperar as apólices a vencer.");
        })
        .finally(() => {
          this.$emit("update-loading", false);
        });
    },

    getTopClients() {
      this.$emit("update-loading", true);
      const params = { year: this.selectedYear };
      if (this.selectedClientPeriod) params.period_days = this.selectedClientPeriod;
      policyService
        .getTopClients(params)
        .then((response) => {
          this.top_clients = response.data.clients || [];
        })
        .catch(() => {
          this.$emit("show-error", "Não foi possível recuperar os top clientes.");
        })
        .finally(() => {
          this.$emit("update-loading", false);
        });
    },

    setClientPeriod(days) {
      this.selectedClientPeriod = days;
      this.selectedClientPeriodLabel = days ? `${days} dias` : "Total";
      this.getTopClients();
    },

    setTopPremiumPeriod(days) {
      this.selectedTopPremiumPeriodDays = days;
      this.selectedTopPremiumPeriodLabel = days ? `${days} dias` : "Total";
      this.useCustomPeriod = false;
      this.getDashboardPoliciesDue();
    },

    getMonthlyCommissionSummary() {
      this.$emit("update-loading", true);
      policyService
        .getMonthlyCommissionSummary({ year: this.selectedYear })
        .then((response) => {
          this.monthly_summary = response.data.summary || [];
          this.total_commission = response.data.total_commission || 0;
          this.total_policies = response.data.total_policies || 0;
        })
        .catch(() => {
          this.$emit("show-error", "Não foi possível recuperar o resumo mensal.");
        })
        .finally(() => {
          this.$emit("update-loading", false);
        });
    },

    getUpcomingRenewals() {
      this.$emit("update-loading", true);
      policyService
        .getUpcomingRenewalPolicies({ limit: this.upcomingRenewalsLimit })
        .then((response) => {
          this.upcoming_renewals = response.data.policies || [];
          this.has_more_renewals = response.data.has_more || false;
        })
        .catch(() => {
          this.$emit("show-error", "Não foi possível recuperar as apólices próximas da renovação.");
        })
        .finally(() => {
          this.$emit("update-loading", false);
        });
    },

    loadMoreUpcomingRenewals() {
      this.upcomingRenewalsLimit += 5;
      this.getUpcomingRenewals();
    },

    getDaysBadgeClass(days) {
      if (days <= 4) return 'status-badge-red';
      if (days <= 7) return 'status-badge-orange';
      if (days <= 12) return 'status-badge-orange-66';
      if (days <= 14) return 'status-badge-orange-33';
      return 'status-badge-yellow';
    },

    getStatusBadgeClass(status) {
      switch (status) {
        case 9:
          return 'status-badge-red';
        case 16:
          return 'status-badge-green';
        case 18:
          return 'status-badge-yellow';
        case 15:
          return 'status-badge-orange-33';
        case 2:
          return 'status-badge-orange-66';
        default:
          return 'status-badge-orange';
      }
    },

    formatCurrencyLarge(value) {
      if (!value) return 'R$ 0,00';
      if (value >= 1000000) {
        const millions = (value / 1000000).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
        return `R$ ${millions} milhões`;
      }
      if (value >= 1000) {
        const thousands = (value / 1000).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
        return `R$ ${thousands} mil`;
      }
      return this.formatCurrency(value);
    },

    redirect(routeName, params = null) {
      this.$router.push({ name: routeName, params: params });
    },

    getNameColumn(column) {
      switch (column) {
        case 1:  return "Renovação próxima";
        case 2:  return "Aguardando contato";
        case 15: return "Aguardando documentação";
        default:
          return this.getEnumKeyByEnumValue(this.translateStatusQuotationTypes, column);
      }
    },

    applyCustomPeriod() {
      this.periodModalVisible = true;
    },

    confirmCustomPeriod() {
      if (!this.customPeriodStart || !this.customPeriodEnd) {
        this.$emit("show-error", "Informe a Data de Início e a Data de Fim.");
        return;
      }

      if (this.customPeriodStart > this.customPeriodEnd) {
        this.$emit("show-error", "A Data de Início não pode ser maior que a Data de Fim.");
        return;
      }

      this.useCustomPeriod = true;
      this.selectedTopPremiumPeriodDays = null;
      this.selectedTopPremiumPeriodLabel = "Total";
      this.periodModalVisible = false;
      this.getDashboardPoliciesDue();
    },

    isClientHolder() {
      return authenticationService.isClientHolder();
    },
  },
};
</script>

<style scoped>
.top-summary-card {
  border-radius: 0;
  border: 0;
}

.period-switch {
  display: inline-flex;
  background-color: #fff;
  border: 1px solid #7f8a99;
  border-radius: 999px;
  overflow: hidden;
  white-space: nowrap;
}

.period-switch-btn {
  border: 0;
  border-right: 1px solid #7f8a99;
  border-radius: 0;
  background-color: #fff;
  color: #13263f;
  font-size: 14px;
  padding: 10px 24px;
  line-height: 1;
}

.period-switch-btn:last-child {
  border-right: 0;
}

.period-switch-btn--active {
  background-color: #13263f;
  color: #fff;
}

.period-switch-btn--disabled {
  background-color: #98a2ad;
  color: #fff;
}

.period-switch-btn--today {
  background-color: #98a2ad;
  color: #fff;
}

.btn-icon-table {
  color: #6c757d;
  background: transparent;
  border: none;
  font-size: 15px;
  padding: 2px 6px;
}

.btn-icon-table:hover {
  color: #007bff;
}

.monthly-total-row td {
  background-color: #f8f9fa !important;
  font-weight: 600;
}

.period-modal-label {
  display: block;
  font-size: 18px;
  font-weight: 500;
  color: #495057;
  margin-bottom: 8px;
}
</style>
